import * as Sentry from "@sentry/react"
import {Replay} from "@sentry/replay"
import {isSentryReplayAllowed, parseStrictPositiveInt} from "helpers/sentryReplayAllowlist"

const TENANT_TAG = "tenant"

/** Identity that owns the currently running replay, if any (`tenant:userId`). */
let activeReplayIdentity: string | null = null

/**
 * Bumped on every disable and on every async identity handoff schedule.
 * Pending stop().then(start) callbacks must match this generation or they are no-ops —
 * otherwise a logout/non-allowlisted switch can race and re-start recording.
 */
let handoffGeneration = 0

function getReplayIntegration(): Replay | null {
    const client = Sentry.getCurrentHub().getClient()
    if (!client) {
        return null
    }
    return client.getIntegration(Replay)
}

/**
 * Stable key for (tenant, userId). Null when either side is unusable —
 * callers must not start a replay without a key.
 */
export function buildSentryReplayIdentityKey(
    tenantSlug: string | null | undefined,
    userId: number | string | null | undefined
): string | null {
    if (typeof tenantSlug !== "string") {
        return null
    }
    const tenant = tenantSlug.trim().toLowerCase()
    if (tenant.length === 0) {
        return null
    }
    const id = parseStrictPositiveInt(userId)
    if (id === null) {
        return null
    }
    return `${tenant}:${id}`
}

/** True iff a scheduled handoff may still start (not cancelled by disable/newer handoff). */
export function isReplayHandoffCurrent(
    scheduledGeneration: number,
    currentGeneration: number = handoffGeneration
): boolean {
    return scheduledGeneration === currentGeneration
}

function clearTenantTag(): void {
    Sentry.setTag(TENANT_TAG, undefined)
}

function startReplayForIdentity(replay: Replay, identityKey: string): void {
    try {
        replay.start()
        activeReplayIdentity = identityKey
    } catch {
        // Already recording or SDK not ready — ignore
        activeReplayIdentity = null
    }
}

/**
 * Start Session Replay only when tenant + user are allowlisted.
 * Sample rates stay 0 in Sentry.init; Replay.start() forces a session.
 *
 * 🚨 Fail-closed on identity change: if the new session is NOT allowlisted, stop any
 * in-flight replay and clear the tenant tag. If it IS allowlisted but for a DIFFERENT
 * identity than the one that started the current replay, stop and start a fresh session
 * so one recording never spans two users/tenants.
 *
 * 🚨 Async handoffs are generation-gated: disable() or a newer handoff invalidates any
 * pending stop().then(start) so a race cannot revive recording after logout.
 */
export function enableSentryReplayIfAllowed(
    tenantSlug: string | null | undefined,
    userId: number | string | null | undefined
): void {
    if (!isSentryReplayAllowed({tenantSlug, userId})) {
        disableSentryReplay()
        return
    }

    const identityKey = buildSentryReplayIdentityKey(tenantSlug, userId)
    if (identityKey === null) {
        disableSentryReplay()
        return
    }

    const slug = (tenantSlug as string).trim().toLowerCase()
    Sentry.setTag(TENANT_TAG, slug)

    const replay = getReplayIntegration()
    if (!replay) {
        return
    }

    const alreadyRecording = Boolean(replay.getReplayId())
    if (alreadyRecording && activeReplayIdentity === identityKey) {
        return
    }

    if (alreadyRecording) {
        // Different allowlisted identity in the same tab — do not mix sessions.
        const scheduledGeneration = ++handoffGeneration
        void replay
            .stop()
            .catch(() => undefined)
            .then(() => {
                if (!isReplayHandoffCurrent(scheduledGeneration)) {
                    return
                }
                startReplayForIdentity(replay, identityKey)
            })
        return
    }

    startReplayForIdentity(replay, identityKey)
}

export function disableSentryReplay(): void {
    // Invalidate any in-flight stop→start handoff before tearing down.
    handoffGeneration += 1
    clearTenantTag()
    activeReplayIdentity = null

    const replay = getReplayIntegration()
    if (!replay) {
        return
    }
    if (!replay.getReplayId()) {
        return
    }
    void replay.stop().catch(() => undefined)
}
